import * as React from 'react'
import { Table, Card, Button } from 'antd'
import type { ColumnsType, TableProps } from 'antd/es/table'
import { EditOutlined } from '@ant-design/icons'
import { path, useColumnLocalSort, useTableSearchParams } from 'src/libs'
import { ErrorScreen } from 'src/shared/components/errorScreen/errorScreen'
import { setWalletsRequestError, walletsMounted } from './model'
import { useWalletsWidget } from './hooks'
import { Wallet } from './types'

export const WalletsWidget = () => {
  const { wallets, partnerWallet, errorWalletsRequest, partnerWalletError } =
    useWalletsWidget()

  const sorterFields = [
    'walletId',
    'balance',
    'currency',
    'createdAt',
    'updatedAt',
  ]

  const handleTableChange: TableProps<Wallet>['onChange'] =
    useTableSearchParams({ sorterFields })

  const columns: ColumnsType<Wallet> = [
    {
      title: 'Wallet ID',
      dataIndex: 'walletId',
      key: 'walletId',
      ...useColumnLocalSort<Wallet>({ dataIndex: 'walletId' }),
      sorter: (a, b) => String(a.walletId).localeCompare(String(b.walletId)),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      ...useColumnLocalSort<Wallet>({ dataIndex: 'balance' }),
      sorter: (a, b) => Number(a.balance) - Number(b.balance),
      render: (val: number, row: Wallet) =>
        (val / (row?.balanceDivider || 1)).toFixed(2),
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      ...useColumnLocalSort<Wallet>({ dataIndex: 'currency' }),
      sorter: (a, b) => a.currency?.localeCompare(b.currency),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      ...useColumnLocalSort<Wallet>({ dataIndex: 'createdAt' }),
      sorter: (a, b) => a.createdAt?.localeCompare(b.createdAt),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      ...useColumnLocalSort<Wallet>({ dataIndex: 'updatedAt' }),
      sorter: (a, b) => a.updatedAt?.localeCompare(b.updatedAt),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: unknown, item: Wallet) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          href={path.clients?.walletDetail(item.clientId, item.walletId)}
        />
      ),
    },
  ]

  if (
    ![0, 404].includes(errorWalletsRequest.code) ||
    ![0, 404, null].includes(partnerWalletError?.code || null)
  ) {
    return (
      <ErrorScreen
        showRetry
        onRetry={() => {
          walletsMounted()
          setWalletsRequestError({
            message: '',
            code: 0,
          })
        }}
      />
    )
  }

  const data: Wallet[] = [
    {
      balance: partnerWallet.withdrawalAmount,
      balanceDivider: 1,
      walletId: partnerWallet.id + ' Partner',
      clientId: partnerWallet.id,
      currency: 'USD',
      createdAt: partnerWallet.createdAt,
      updatedAt: partnerWallet.updatedAt,
    },
    ...(wallets || []),
  ]

  return (
    <Card title="Wallets list" variant="borderless">
      <Table<Wallet>
        columns={columns}
        dataSource={data}
        rowKey="walletId"
        size="middle"
        onChange={handleTableChange}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        style={{ fontSize: 18 }}
      />
    </Card>
  )
}
